import {
  ReactNode,
  RefObject,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import Editor, { EditorPlugin } from '@draft-js-plugins/editor';
import { DraftEditorCommand, EditorState, RichUtils } from 'draft-js';
import styled from 'styled-components';

import { theme } from '@styles/theme';
import {
  getSelectedBlock,
  isEditorEmpty,
  makeEditorState,
  editorStateToHtml,
} from '@utils/draftjs';
import { transitionStandard } from '@utils/transitions';
import { isDefined } from '@utils/types';

import { createAnchorPlugin } from './plugin-anchor';
import { TextEditorPluginMention } from './plugin-mention';
import {
  PlaceholderEditorPlugin,
  TextEditorPluginPlaceholder,
  convertPlaceholders,
} from './plugin-placeholder';
import { TextEditorMessageTemplate } from './plugin-template-message';
import { TextEditorPluginToolbar } from './plugin-toolbar';

type RegisterPluginFn = (name: TextEditorPlugin, plugin: EditorPlugin) => void;
type RegisteredPlugins = Partial<{
  toolbar: EditorPlugin;
  mention: EditorPlugin;
  anchor: EditorPlugin;
  placeholder: PlaceholderEditorPlugin;
}>;
type TextEditorPlugin = 'toolbar' | 'mention' | 'placeholder';
type TextEditorVariant = 'gray' | 'orange' | 'white' | 'green' | 'red';
interface TextEditorContextType {
  editorState: EditorState;
  isFocused: boolean;
  setIsFocused: (value: boolean) => void;
  setEditorState: (value: EditorState) => void;
  editorRef: RefObject<Editor>;
  containerRef: RefObject<HTMLDivElement>;
  registerPlugin: RegisterPluginFn;
  activePlugins: Array<EditorPlugin>;
  readOnly: boolean;
  disabled: boolean;
  error: boolean;
}

const TextEditorContext = createContext({} as TextEditorContextType);

export interface TextEditorProps {
  children: ReactNode;
  variant: TextEditorVariant;
  editorState?: EditorState;
  initialEditorState?: string | EditorState;
  onChange?: (newEditorState: EditorState) => void;
  onChangeHtml?: (html: string) => void;
  readOnly?: boolean;
  focusOnMount?: boolean;
  error?: boolean;
  disabled?: boolean;
}

export function TextEditor({
  children,
  variant,
  readOnly = false,
  focusOnMount = false,
  disabled = false,
  error = false,
  initialEditorState = EditorState.createEmpty(),
  editorState: userEditorState,
  onChange,
  onChangeHtml,
}: TextEditorProps) {
  const editorRef = useRef<Editor>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isFocused, setIsFocused] = useState(false);
  const [_editorState, setEditorState] = useState(makeEditorState(initialEditorState));

  const editorState = userEditorState || _editorState;

  const [registeredPlugins, setRegisteredPlugins] = useState<RegisteredPlugins>({
    anchor: createAnchorPlugin(),
  });

  const registerPlugin = useCallback<RegisterPluginFn>((name, plugin) => {
    setRegisteredPlugins((old) => ({ ...old, [name]: plugin }));
  }, []);

  const activePlugins = useMemo<Array<EditorPlugin>>(() => {
    return Object.values(registeredPlugins).filter(isDefined);
  }, [registeredPlugins]);

  const onEditorStateChange = useCallback(
    (editorState: EditorState) => {
      let es = editorState;
      if (registeredPlugins.placeholder) {
        es = convertPlaceholders(editorState, registeredPlugins.placeholder.placeholderMap);
      }
      setEditorState(es);
      onChange?.(es);
      window.requestAnimationFrame(() => onChangeHtml?.(editorStateToHtml(es)));
    },
    [registeredPlugins, setEditorState, onChange, onChangeHtml]
  );

  useEffect(() => {
    if (focusOnMount) {
      const to = setTimeout(() => {
        editorRef.current?.focus();
        setIsFocused(true);
      }, 0);
      return () => clearTimeout(to);
    }
  }, [focusOnMount]);

  useEffect(() => {
    function listener(event: MouseEvent) {
      if (containerRef.current?.contains(event.target as Node)) {
        if (!isFocused) {
          editorRef.current?.focus();
          setIsFocused(true);
        }
      } else {
        if (isFocused) setIsFocused(false);
      }
    }

    document.addEventListener('click', listener);

    return () => {
      document.removeEventListener('click', listener);
    };
  }, [isFocused]);

  return (
    <TextEditorContainer
      ref={containerRef}
      data-variant={variant}
      data-focused={isFocused}
      data-readonly={readOnly}
      data-error={error}
    >
      <TextEditorContext.Provider
        value={{
          activePlugins,
          containerRef,
          disabled,
          editorRef,
          editorState,
          error,
          isFocused,
          readOnly,
          registerPlugin,
          setEditorState: onEditorStateChange,
          setIsFocused,
        }}
      >
        {children}
      </TextEditorContext.Provider>
    </TextEditorContainer>
  );
}

function EditorActions({ children }: { children: ReactNode }) {
  return <EditorActionsContainer>{children}</EditorActionsContainer>;
}

export function useTextEditorContext() {
  return useContext(TextEditorContext);
}

function EditorInput({
  placeholder,
  stripPastedStyles = false,
  disableShortcuts = false,
  collapseMargin = false,
  dataCy = 'text-editor',
}: {
  placeholder?: string;
  stripPastedStyles?: boolean;
  disableShortcuts?: boolean;
  collapseMargin?: boolean;
  dataCy?: string;
}) {
  const { activePlugins, editorRef, editorState, setEditorState, readOnly, disabled } =
    useTextEditorContext();

  let showPlaceholder = placeholder && isEditorEmpty(editorState);
  if (showPlaceholder) {
    const currentType = getSelectedBlock(editorState)?.getType();
    showPlaceholder = currentType !== 'unordered-list-item' && currentType !== 'ordered-list-item';
  }

  const handleKeyCommand = (command: DraftEditorCommand) => {
    const newState = RichUtils.handleKeyCommand(editorState, command);
    if (newState) {
      setEditorState(newState);
      return 'handled';
    }
    return 'not-handled';
  };

  return (
    <EditorInputContainer data-cy={dataCy} $collapseMargin={collapseMargin}>
      {showPlaceholder && <PlaceholderMessage>{placeholder}</PlaceholderMessage>}
      <Editor
        stripPastedStyles={stripPastedStyles}
        key={activePlugins.toString()}
        ref={editorRef}
        handleKeyCommand={disableShortcuts ? undefined : handleKeyCommand}
        editorState={editorState}
        onChange={setEditorState}
        plugins={activePlugins}
        readOnly={readOnly || disabled}
      />
    </EditorInputContainer>
  );
}

const EditorInputContainer = styled.div<{ $collapseMargin: boolean }>((p) => ({
  position: 'relative',
  ...(p.$collapseMargin && { margin: '-1rem 0' }),
}));

const EditorActionsContainer = styled.div({
  minHeight: '2.5rem',
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '0.5rem',
  '@media(max-width: 768px)': {
    flexDirection: 'column',
  },
});

const PlaceholderMessage = styled.div({
  pointerEvents: 'none',
  position: 'absolute',
  left: 0,
  top: 0,
  color: theme.color.purple50,
});

const TextEditorContainer = styled.div({
  cursor: 'text',
  width: '100%',
  transition: transitionStandard('all'),
  border: '0.0625rem solid transparent',
  borderRadius: '0.25rem',
  font: theme.font.regular16,
  color: theme.color.textPrimary,
  padding: '1rem',

  a: {
    color: theme.color.elementBlue100 + ' !important',
    textDecoration: 'underline !important',
  },

  '.public-DraftStyleDefault-block': {
    wordBreak: 'break-word',
  },

  ol: {
    marginBottom: 5,
    paddingInlineStart: 25,
  },

  ul: {
    marginBottom: 5,
    paddingInlineStart: 25,
  },

  '&[data-readonly="false"]': {
    '.public-DraftEditor-content': {
      minHeight: '6rem',
      margin: '1rem 0',
    },
  },

  '&[data-variant="green"]': {
    background: theme.color.green10,
    color: theme.color.green200,
    borderColor: theme.color.green200,
  },

  '&[data-variant="red"]': {
    background: theme.color.red10,
    borderColor: theme.color.red80,
    color: theme.color.red100,
  },

  '&[data-variant="gray"]': {
    background: theme.color.uiTrans5,

    '&[data-readonly="false"]': {
      '&[data-focused="false"]:hover': {
        background: theme.color.uiTrans10,
      },

      '&[data-focused="true"]': {
        background: theme.color.backgroundLightDark,
        borderColor: theme.color.border100,
      },
    },
  },

  '&[data-variant="orange"]': {
    background: theme.color.orange10,
    borderColor: theme.color.border10,

    '&[data-readonly="true"]': {
      borderColor: theme.color.orange100,
    },

    '&[data-readonly="false"]': {
      '&[data-focused="false"]:hover': {
        borderColor: theme.color.orange80,
      },

      '&[data-focused="true"]': {
        borderColor: theme.color.orange100,
      },
    },
  },

  '&[data-variant="white"]': {
    background: theme.color.backgroundLightDark,
    borderColor: theme.color.border10,

    '&[data-readonly="true"]': {
      borderColor: theme.color.border10,
    },

    '&[data-readonly="false"]': {
      '&[data-focused="false"]:hover': {
        borderColor: theme.color.purple50,
      },

      '&[data-focused="true"]': {
        borderColor: theme.color.border100,
      },
    },
  },

  '&[data-error="true"]': {
    borderColor: theme.color.red100 + ' !important',
  },
});

TextEditor.Input = EditorInput;
TextEditor.Toolbar = TextEditorPluginToolbar;
TextEditor.PlaceholderMenu = TextEditorPluginPlaceholder;
TextEditor.Mention = TextEditorPluginMention;
TextEditor.Actions = EditorActions;
TextEditor.Template = TextEditorMessageTemplate;
